import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import API_BASE_URL from '../../config';
import parseMarkdown from '../../utils/parseMarkdown';
import './agent.css';

/* ── SVG Icons ── */
function Icon({ name }) {
  var paths = {
    home: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1',
    strategy: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6m14 0v-10a2 2 0 00-2-2h-2a2 2 0 00-2 2v10m-2 0h12',
    research: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
    campaigns: 'M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z',
    social: 'M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z',
    analytics: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6m6 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0h6m2 0v-4a2 2 0 012-2h2a2 2 0 012 2v4m-4 0h4',
    content: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
    growth: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6',
    seo: 'M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064',
    history: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
    logout: 'M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1',
    send: 'M12 19l9 2-9-18-9 18 9-2zm0 0v-8',
    back: 'M10 19l-7-7m0 0l7-7m-7 7h18',
    menu: 'M4 6h16M4 12h16M4 18h16',
    chevron: 'M9 5l7 7-7 7',
  };
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d={paths[name] || paths.home} />
    </svg>
  );
}

var MODULES = [
  { key: 'strategy', label: 'Strategy', icon: 'strategy' },
  { key: 'research', label: 'Research', icon: 'research' },
  { key: 'campaigns', label: 'Campaigns', icon: 'campaigns' },
  { key: 'social', label: 'Social', icon: 'social' },
  { key: 'analytics', label: 'Analytics', icon: 'analytics' },
  { key: 'content', label: 'Content', icon: 'content' },
  { key: 'growth', label: 'Growth', icon: 'growth' },
  { key: 'seo', label: 'SEO / SEM', icon: 'seo' },
];

var VIEWS = { HOME: 'home', CHAT: 'chat', HISTORY: 'history' };
var CHART_COLORS = ['#d46a28', '#f0a060', '#34c759', '#5ac8fa', '#af52de'];

function timeAgo(d) {
  var diff = Date.now() - new Date(d).getTime();
  var m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return m + 'm ago';
  var h = Math.floor(m / 60);
  if (h < 24) return h + 'h ago';
  return Math.floor(h / 24) + 'd ago';
}

function Dashboard() {
  var { projectId } = useParams();
  var navigate = useNavigate();
  var token = localStorage.getItem('agent_token');

  var [project, setProject] = useState(null);
  var [view, setView] = useState(VIEWS.HOME);
  var [activeModule, setActiveModule] = useState(null);
  var [sidebarOpen, setSidebarOpen] = useState(true);

  var [messages, setMessages] = useState([]);
  var [input, setInput] = useState('');
  var [loading, setLoading] = useState(false);
  var [convId, setConvId] = useState(null);
  var chatEndRef = useRef(null);

  var [conversations, setConversations] = useState([]);
  var [historyModule, setHistoryModule] = useState(null);
  var [loadingHistory, setLoadingHistory] = useState(false);

  var [stats, setStats] = useState({
    totalConversations: 0, totalMessages: 0, modulesUsed: 0, artifactsCreated: 0,
    recentActivity: [], moduleBreakdown: [], weeklyActivity: [],
  });

  useEffect(function() {
    if (!token) { navigate('/agent'); return; }
    fetch(API_BASE_URL + '/api/projects/' + projectId, {
      headers: { Authorization: 'Bearer ' + token },
    }).then(function(r) { return r.json(); })
      .then(function(d) { if (d.project) setProject(d.project); else navigate('/agent'); })
      .catch(function() { navigate('/agent'); });
  }, [projectId, token, navigate]);

  useEffect(function() {
    if (view !== VIEWS.HOME || !token) return;
    fetch(API_BASE_URL + '/api/chat/' + projectId + '/stats', {
      headers: { Authorization: 'Bearer ' + token },
    }).then(function(r) { return r.json(); })
      .then(function(d) { if (d.stats) setStats(d.stats); })
      .catch(function() {});
  }, [view, projectId, token]);

  useEffect(function() {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  function openModule(mod) { setActiveModule(mod); setView(VIEWS.CHAT); setMessages([]); setConvId(null); setInput(''); }

  function openHistory(mod) {
    setHistoryModule(mod || null); setView(VIEWS.HISTORY); setLoadingHistory(true);
    var url = API_BASE_URL + '/api/chat/' + projectId + '/conversations' + (mod ? '?module=' + mod : '');
    fetch(url, { headers: { Authorization: 'Bearer ' + token } })
      .then(function(r) { return r.json(); })
      .then(function(d) { setConversations(d.conversations || []); setLoadingHistory(false); })
      .catch(function() { setLoadingHistory(false); });
  }

  function resumeConversation(conv) {
    setActiveModule(conv.module); setView(VIEWS.CHAT); setConvId(conv.id); setInput(''); setMessages([]);
    fetch(API_BASE_URL + '/api/chat/conversation/' + conv.id, { headers: { Authorization: 'Bearer ' + token } })
      .then(function(r) { return r.json(); })
      .then(function(d) { setMessages((d.messages || []).filter(function(m) { return m.role !== 'system'; })); });
  }

  async function sendMessage(e) {
    e.preventDefault();
    if (!input.trim() || loading) return;
    var userMsg = input.trim(); setInput('');
    setMessages(function(p) { return p.concat({ role: 'user', content: userMsg }); });
    setLoading(true);
    try {
      var res = await fetch(API_BASE_URL + '/api/chat/' + projectId + '/' + activeModule, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ message: userMsg, conversationId: convId }),
      });
      var data = await res.json();
      if (data.conversationId) setConvId(data.conversationId);
      setMessages(function(p) { return p.concat({ role: 'assistant', content: data.reply || data.error }); });
    } catch (err) {
      setMessages(function(p) { return p.concat({ role: 'assistant', content: 'Error: ' + err.message }); });
    }
    setLoading(false);
  }

  function handleLogout() { localStorage.removeItem('agent_token'); localStorage.removeItem('agent_user'); navigate('/agent'); }

  if (!project) return <div className="d-loading"><div className="d-spinner" /></div>;

  // Build chart data from stats
  var moduleData = (stats.moduleBreakdown && stats.moduleBreakdown.length > 0)
    ? stats.moduleBreakdown
    : [];

  var weeklyData = (stats.weeklyActivity && stats.weeklyActivity.length > 0)
    ? stats.weeklyActivity
    : [];

  var platformData = (project.platforms || []).map(function(p, i) {
    return { name: p, value: Math.floor(Math.random() * 40) + 10 };
  });
  if (platformData.length === 0) platformData = [{ name: 'No platforms', value: 1 }];

  return (
    <div className="d-shell">
      <aside className={'d-sidebar' + (sidebarOpen ? '' : ' collapsed')}>
        <div className="d-sidebar-top">
          <div className="d-brand">
            {sidebarOpen && <span className="d-brand-text">MarketingAI</span>}
            <button className="d-collapse-btn" onClick={function() { setSidebarOpen(!sidebarOpen); }}>
              <Icon name="menu" />
            </button>
          </div>
          {sidebarOpen && (
            <div className="d-project-badge">
              <div className="d-project-avatar">{project.business_name.charAt(0)}</div>
              <div className="d-project-info">
                <span className="d-project-name">{project.business_name}</span>
                <span className="d-project-ind">{project.industry}</span>
              </div>
            </div>
          )}
        </div>

        <nav className="d-nav">
          {sidebarOpen && <div className="d-nav-section">General</div>}
          <button className={'d-nav-item' + (view === VIEWS.HOME ? ' active' : '')} onClick={function() { setView(VIEWS.HOME); }}>
            <Icon name="home" />{sidebarOpen && <span>Dashboard</span>}
          </button>
          <button className={'d-nav-item' + (view === VIEWS.HISTORY ? ' active' : '')} onClick={function() { openHistory(null); }}>
            <Icon name="history" />{sidebarOpen && <span>History</span>}
          </button>

          {sidebarOpen && <div className="d-nav-section">Tools</div>}
          {MODULES.map(function(m) {
            return (
              <button key={m.key} className={'d-nav-item' + (view === VIEWS.CHAT && activeModule === m.key ? ' active' : '')}
                onClick={function() { openModule(m.key); }}>
                <Icon name={m.icon} />{sidebarOpen && <span>{m.label}</span>}
              </button>
            );
          })}
        </nav>

        <div className="d-sidebar-bottom">
          <a href="/" className="d-nav-item d-back-site">
            <Icon name="back" />{sidebarOpen && <span>Back to site</span>}
          </a>
          <button className="d-nav-item d-logout" onClick={handleLogout}>
            <Icon name="logout" />{sidebarOpen && <span>Log out</span>}
          </button>
        </div>
      </aside>

      <main className="d-main">
        {view === VIEWS.HOME && renderHome()}
        {view === VIEWS.CHAT && renderChat()}
        {view === VIEWS.HISTORY && renderHistory()}
      </main>
    </div>
  );

  function renderHome() {
    var TIPS = [
      { icon: 'strategy', title: 'Build a Strategy First', desc: 'Start with the Strategy module to create a marketing roadmap before diving into tactics.' },
      { icon: 'research', title: 'Know Your Audience', desc: 'Use Research to build detailed customer personas and competitor analysis.' },
      { icon: 'content', title: 'Content is King', desc: 'Generate blog posts, ad copy, and email sequences with the Content module.' },
      { icon: 'analytics', title: 'Measure Everything', desc: 'Set up KPI tracking with Analytics to know what\'s working and what\'s not.' },
      { icon: 'seo', title: 'Optimize for Search', desc: 'Use SEO/SEM to find high-value keywords and improve your organic rankings.' },
      { icon: 'growth', title: 'Growth Loops', desc: 'Design referral programs and viral loops with the Growth module.' },
    ];

    var unusedModules = MODULES.filter(function(m) {
      return !(stats.moduleBreakdown || []).some(function(b) { return b.name === m.key; });
    });

    return (
      <div className="d-home">
        <div className="d-home-main">
          <header className="d-topbar">
            <div>
              <h1 className="d-page-title">Dashboard</h1>
              <p className="d-page-sub">{project.business_name} — {project.industry || 'Marketing Overview'}</p>
            </div>
            <div className="d-topbar-right">
              <span className="d-topbar-date">{new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
            </div>
          </header>

          <div className="d-kpi-row">
            <div className="d-kpi-card">
              <div className="d-kpi-header">
                <span className="d-kpi-icon"><Icon name="social" /></span>
                <span className="d-kpi-title">Conversations</span>
              </div>
              <span className="d-kpi-value">{stats.totalConversations}</span>
              <span className="d-kpi-change positive">+{Math.max(stats.totalConversations, 1)} this period</span>
            </div>
            <div className="d-kpi-card">
              <div className="d-kpi-header">
                <span className="d-kpi-icon"><Icon name="content" /></span>
                <span className="d-kpi-title">Total Messages</span>
              </div>
              <span className="d-kpi-value">{stats.totalMessages}</span>
              <span className="d-kpi-change positive">Active</span>
            </div>
            <div className="d-kpi-card">
              <div className="d-kpi-header">
                <span className="d-kpi-icon"><Icon name="analytics" /></span>
                <span className="d-kpi-title">Modules Used</span>
              </div>
              <span className="d-kpi-value">{stats.modulesUsed}<span className="d-kpi-total">/{MODULES.length}</span></span>
              <span className="d-kpi-change">{Math.round((stats.modulesUsed / MODULES.length) * 100)}% coverage</span>
            </div>
            <div className="d-kpi-card">
              <div className="d-kpi-header">
                <span className="d-kpi-icon"><Icon name="growth" /></span>
                <span className="d-kpi-title">Artifacts</span>
              </div>
              <span className="d-kpi-value">{stats.artifactsCreated || 0}</span>
              <span className="d-kpi-change">Generated content</span>
            </div>
          </div>

          <div className="d-charts-row">
            <div className="d-chart-card d-chart-card--wide">
              <div className="d-chart-header"><h3>Weekly Activity</h3></div>
              <div className="d-chart-body">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={weeklyData} barGap={4}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e5ea" vertical={false} />
                    <XAxis dataKey="day" tick={{ fontSize: 12, fill: '#8e8e93' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 12, fill: '#8e8e93' }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e5e5ea', fontSize: 13 }} />
                    <Bar dataKey="messages" fill="#d46a28" radius={[4,4,0,0]} name="Messages" />
                    <Bar dataKey="conversations" fill="#f0a060" radius={[4,4,0,0]} name="Conversations" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="d-chart-card">
              <div className="d-chart-header"><h3>Platform Distribution</h3></div>
              <div className="d-chart-body d-chart-body--pie">
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={platformData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={3} dataKey="value">
                      {platformData.map(function(entry, i) { return <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />; })}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e5e5ea', fontSize: 13 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="d-pie-legend">
                  {platformData.map(function(p, i) {
                    return (<div key={i} className="d-pie-legend-item"><span className="d-pie-dot" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} /><span>{p.name}</span></div>);
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="d-charts-row">
            <div className="d-chart-card">
              <div className="d-chart-header"><h3>Module Usage</h3></div>
              <div className="d-chart-body">
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={moduleData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e5ea" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#8e8e93' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 12, fill: '#8e8e93' }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e5e5ea', fontSize: 13 }} />
                    <Line type="monotone" dataKey="value" stroke="#d46a28" strokeWidth={2.5} dot={{ fill: '#d46a28', r: 4 }} name="Sessions" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="d-chart-card">
              <div className="d-chart-header">
                <h3>Recent Activity</h3>
                <button className="d-text-btn" onClick={function() { openHistory(null); }}>See All</button>
              </div>
              <div className="d-table-wrap">
                <table className="d-table">
                  <thead><tr><th>Module</th><th>Topic</th><th>Time</th></tr></thead>
                  <tbody>
                    {(stats.recentActivity && stats.recentActivity.length > 0) ? stats.recentActivity.slice(0, 5).map(function(a, i) {
                      var mod = MODULES.find(function(m) { return m.key === a.module; });
                      return (
                        <tr key={i} className="d-table-row-click" onClick={function() { resumeConversation(a); }}>
                          <td><span className="d-table-badge">{mod ? mod.label : a.module}</span></td>
                          <td className="d-table-topic">{a.title || 'Untitled'}</td>
                          <td className="d-table-time">{timeAgo(a.updated_at)}</td>
                        </tr>
                      );
                    }) : (
                      <tr><td colSpan="3" className="d-table-empty">No activity yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="d-chart-card">
            <div className="d-chart-header"><h3>Project Details</h3></div>
            <div className="d-details-grid">
              <div className="d-detail"><span className="d-detail-label">Website</span><span>{project.website_url || '—'}</span></div>
              <div className="d-detail"><span className="d-detail-label">Budget</span><span>{project.monthly_budget || '—'}</span></div>
              <div className="d-detail"><span className="d-detail-label">Brand Voice</span><span>{project.brand_voice || '—'}</span></div>
              <div className="d-detail"><span className="d-detail-label">Audience</span><span>{project.target_audience || '—'}</span></div>
              <div className="d-detail"><span className="d-detail-label">Goals</span><span>{(project.goals || []).join(', ') || '—'}</span></div>
              <div className="d-detail"><span className="d-detail-label">Competitors</span><span>{(project.competitors || []).join(', ') || '—'}</span></div>
            </div>
          </div>
        </div>

        <aside className="d-home-aside">
          {/* Quick Actions */}
          <div className="d-aside-card">
            <h4 className="d-aside-title">Quick Actions</h4>
            <div className="d-aside-actions">
              {MODULES.slice(0, 4).map(function(m) {
                return (
                  <button key={m.key} className="d-aside-action" onClick={function() { openModule(m.key); }}>
                    <Icon name={m.icon} /><span>{m.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Try These Modules */}
          {unusedModules.length > 0 && (
            <div className="d-aside-card">
              <h4 className="d-aside-title">Try These Modules</h4>
              <div className="d-aside-suggestions">
                {unusedModules.slice(0, 3).map(function(m) {
                  return (
                    <button key={m.key} className="d-aside-suggestion" onClick={function() { openModule(m.key); }}>
                      <span className="d-aside-suggestion-icon"><Icon name={m.icon} /></span>
                      <span className="d-aside-suggestion-text">{m.label}</span>
                      <span className="d-aside-suggestion-arrow"><Icon name="chevron" /></span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Marketing Tips */}
          <div className="d-aside-card">
            <h4 className="d-aside-title">Marketing Tips</h4>
            <div className="d-aside-tips">
              {TIPS.slice(0, 3).map(function(tip, i) {
                return (
                  <div key={i} className="d-aside-tip">
                    <span className="d-aside-tip-icon"><Icon name={tip.icon} /></span>
                    <div>
                      <span className="d-aside-tip-title">{tip.title}</span>
                      <p className="d-aside-tip-desc">{tip.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Getting Started Checklist */}
          <div className="d-aside-card">
            <h4 className="d-aside-title">Getting Started</h4>
            <div className="d-aside-checklist">
              <div className={'d-aside-check' + (stats.totalConversations > 0 ? ' done' : '')}>
                <span className="d-aside-check-box">{stats.totalConversations > 0 ? '✓' : ''}</span>
                <span>Start your first conversation</span>
              </div>
              <div className={'d-aside-check' + (stats.modulesUsed >= 3 ? ' done' : '')}>
                <span className="d-aside-check-box">{stats.modulesUsed >= 3 ? '✓' : ''}</span>
                <span>Explore 3 different modules</span>
              </div>
              <div className={'d-aside-check' + ((stats.artifactsCreated || 0) > 0 ? ' done' : '')}>
                <span className="d-aside-check-box">{(stats.artifactsCreated || 0) > 0 ? '✓' : ''}</span>
                <span>Generate your first artifact</span>
              </div>
              <div className={'d-aside-check' + (stats.totalMessages >= 20 ? ' done' : '')}>
                <span className="d-aside-check-box">{stats.totalMessages >= 20 ? '✓' : ''}</span>
                <span>Send 20+ messages</span>
              </div>
            </div>
          </div>
        </aside>
      </div>
    );
  }

  function renderChat() {
    var mod = MODULES.find(function(m) { return m.key === activeModule; });
    return (
      <div className="d-chat">
        <header className="d-chat-top">
          <button className="d-back-btn" onClick={function() { setView(VIEWS.HOME); }}><Icon name="back" /> <span>Back</span></button>
          <div className="d-chat-title-row">
            {mod && <Icon name={mod.icon} />}
            <h2 className="d-chat-title">{mod ? mod.label : ''}</h2>
          </div>
          <button className="d-text-btn" onClick={function() { openHistory(activeModule); }}>History</button>
        </header>
        <div className="d-chat-body">
          {messages.length === 0 && !loading && (
            <div className="d-chat-welcome">
              <div className="d-chat-welcome-icon">{mod && <Icon name={mod.icon} />}</div>
              <h3>Start a conversation</h3>
              <p>Ask anything about {mod ? mod.label.toLowerCase() : ''} for {project.business_name}.</p>
            </div>
          )}
          {messages.map(function(msg, i) {
            return (
              <div key={i} className={'d-msg ' + msg.role}>
                <div className="d-msg-avatar">{msg.role === 'user' ? 'U' : 'AI'}</div>
                <div className="d-msg-content">
                  {msg.role === 'assistant'
                    ? <div className="d-msg-bubble d-msg-md" dangerouslySetInnerHTML={{ __html: parseMarkdown(msg.content) }} />
                    : <div className="d-msg-bubble">{msg.content}</div>
                  }
                </div>
              </div>
            );
          })}
          {loading && (
            <div className="d-msg assistant">
              <div className="d-msg-avatar">AI</div>
              <div className="d-msg-content"><div className="d-msg-bubble d-typing"><span /><span /><span /></div></div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>
        <form className="d-chat-bar" onSubmit={sendMessage}>
          <input className="d-chat-input" placeholder="Type your message…"
            value={input} onChange={function(e) { setInput(e.target.value); }} disabled={loading} />
          <button type="submit" className="d-send-btn" disabled={loading || !input.trim()}>
            <Icon name="send" />
          </button>
        </form>
      </div>
    );
  }

  function renderHistory() {
    return (
      <div className="d-history">
        <header className="d-topbar">
          <div>
            <button className="d-back-btn" onClick={function() { setView(VIEWS.HOME); }}><Icon name="back" /> <span>Back</span></button>
            <h1 className="d-page-title">Conversation History</h1>
          </div>
        </header>
        <div className="d-history-filters">
          <button className={'d-filter-chip' + (!historyModule ? ' active' : '')} onClick={function() { openHistory(null); }}>All</button>
          {MODULES.map(function(m) {
            return <button key={m.key} className={'d-filter-chip' + (historyModule === m.key ? ' active' : '')}
              onClick={function() { openHistory(m.key); }}>{m.label}</button>;
          })}
        </div>
        {loadingHistory ? <div className="d-loading"><div className="d-spinner" /></div> : conversations.length === 0 ? (
          <p className="d-empty">No conversations yet{historyModule ? ' in this module' : ''}.</p>
        ) : (
          <div className="d-table-wrap d-history-table">
            <table className="d-table">
              <thead><tr><th>Module</th><th>Topic</th><th>Last Active</th><th></th></tr></thead>
              <tbody>
                {conversations.map(function(c) {
                  var mod = MODULES.find(function(m) { return m.key === c.module; });
                  return (
                    <tr key={c.id} className="d-table-row-click" onClick={function() { resumeConversation(c); }}>
                      <td><span className="d-table-badge">{mod ? mod.label : c.module}</span></td>
                      <td className="d-table-topic">{c.title || 'Untitled'}</td>
                      <td className="d-table-time">{timeAgo(c.updated_at)}</td>
                      <td><Icon name="chevron" /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }
}

export default Dashboard;
